/**
 * Client-error telemetry — Phase 3a hardening (iter 469).
 *
 * Ships JS errors (from ErrorBoundary + window.onerror +
 * window.onunhandledrejection) to the backend so admins have
 * visibility into frontend crashes. Best-effort — never throws,
 * never blocks the render path, gracefully deduplicates so a hot
 * render loop doesn't hammer the server.
 */
const API = process.env.REACT_APP_BACKEND_URL || "";
const ENDPOINT = `${API}/api/telemetry/client-error`;

// Recent-error dedupe. We keep 20 hashes for 60s so a component
// re-render that keeps throwing the same error doesn't create 1000
// audit rows. The hash is a cheap join of message + first stack line.
const _recent = new Map();
const DEDUPE_TTL_MS = 60_000;
const DEDUPE_MAX = 20;

const _hash = (msg, stack) => {
  const first = (stack || "").split("\n")[0] || "";
  return `${msg}\u0001${first}`;
};

const _seenRecently = (hash) => {
  const now = Date.now();
  // Evict expired entries; also drop oldest if capacity exceeded.
  for (const [k, ts] of _recent) {
    if (now - ts > DEDUPE_TTL_MS) _recent.delete(k);
  }
  if (_recent.has(hash)) return true;
  if (_recent.size >= DEDUPE_MAX) {
    // Drop oldest — Map preserves insertion order so first key is oldest.
    const oldest = _recent.keys().next().value;
    if (oldest !== undefined) _recent.delete(oldest);
  }
  _recent.set(hash, now);
  return false;
};

/**
 * Ship an error to the backend. Silent on failure — this is best-
 * effort telemetry, not a critical path.
 */
export const reportClientError = async ({
  message,
  stack,
  componentStack,
  source = "react",
  context = null,
} = {}) => {
  try {
    if (!message) return;
    const h = _hash(message, stack);
    if (_seenRecently(h)) return;
    const token = localStorage.getItem("token");
    // Use fetch (not axios) so we don't pull in auth interceptors —
    // this endpoint accepts anonymous.
    await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: String(message).slice(0, 2000),
        stack: stack ? String(stack).slice(0, 8000) : null,
        component_stack: componentStack ? String(componentStack).slice(0, 8000) : null,
        source,
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        context,
      }),
      // Keepalive so the beacon still fires if the user navigates away.
      keepalive: true,
    });
  } catch { /* silently swallow — telemetry must never crash the app */ }
};

/**
 * Install once at app startup. Wires window.onerror +
 * onunhandledrejection to the telemetry endpoint.
 */
export const installGlobalErrorHandlers = () => {
  if (typeof window === "undefined") return;
  if (window.__avidaErrorHandlersInstalled) return;
  window.__avidaErrorHandlersInstalled = true;

  window.addEventListener("error", (event) => {
    // Non-JS errors (image 404s, resource loads) surface here with
    // no `message`. Filter to keep signal high.
    if (!event?.message) return;
    reportClientError({
      message: event.message,
      stack: event.error?.stack,
      source: "window_onerror",
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const message = reason?.message || String(reason || "unhandled promise rejection");
    reportClientError({
      message,
      stack: reason?.stack,
      source: "window_onunhandledrejection",
    });
  });
};
